import { type MutableRefObject, useEffect, useRef } from "react";

import { emitEvent, uploadSnapshot } from "@/lib/api";

import {
  createAntiCheat,
  type AntiCheatController,
  type AntiCheatPayload,
  type PastePayload,
} from "..";
import {
  type CameraMonitor,
  type SnapshotConfig,
  createCameraMonitor,
  disposeActiveStream,
  getActiveStream,
  resumeCameraStream,
} from "../camera";

export interface AntiCheatSwitches {
  camera: boolean;
  activity: boolean;
  // FEAT-38: interaction context available for this attempt (/preview's
  // answer, cached by the Gate). Omitted = off. Rides `activity`.
  interaction?: boolean;
}

const ALL_ON: AntiCheatSwitches = { camera: true, activity: true };

export interface SnapshotOptions {
  // EF-8: the schedule the server froze for this attempt, read lazily by the
  // monitor on every tick (it arrives with the first /question payload, after
  // the monitor started). null = no photos.
  getConfig: () => SnapshotConfig | null;
}

export function useAntiCheat(
  token: string,
  attemptId: string | null,
  getQuestionId?: () => string | undefined,
  // Fires after every recorded paste (clipboard or drop, in or out of the
  // editor) once the native dispatch has completed — the seam Question.tsx
  // uses for the "Copy & pasting detected" notice. Held in a ref like
  // getQuestionId so the controller (created once per attempt) always calls
  // the latest closure without re-creating itself on every render.
  onPasteRecorded?: (payload: PastePayload) => void,
  // EF-8: when given, the camera monitor takes a photo every `interval_s`
  // plus one on the extras (several faces, a face returning, a fullscreen
  // exit, a tab return) and POSTs it to /api/t/<token>/snapshots. The
  // uploader and the controller → monitor signal wiring live here, so the
  // page never touches the monitor.
  snapshots?: SnapshotOptions,
  // FEAT-25: the two per-test switches this hook owns, as the attempt froze
  // them. `null` = not known yet (the first /question payload has not landed
  // and nothing is cached): the hook then creates NOTHING — no listener, and
  // above all no camera prompt for a test that has none. Omitting the
  // argument keeps the pre-FEAT-25 behaviour (both on).
  switches: AntiCheatSwitches | null = ALL_ON,
): MutableRefObject<AntiCheatController | null> {
  const controllerRef = useRef<AntiCheatController | null>(null);
  const getQuestionIdRef = useRef(getQuestionId);
  getQuestionIdRef.current = getQuestionId;
  const onPasteRecordedRef = useRef(onPasteRecorded);
  onPasteRecordedRef.current = onPasteRecorded;
  const snapshotsRef = useRef(snapshots);
  snapshotsRef.current = snapshots;
  const camera = switches?.camera;
  const activity = switches?.activity;
  const interaction = switches?.interaction === true; // FEAT-38

  useEffect(() => {
    if (!attemptId || camera === undefined || activity === undefined) return;
    let monitor: CameraMonitor | null = null;
    const controller = createAntiCheat({
      token,
      attemptId,
      detectors: activity,
      interaction, // FEAT-38: the fold, under the activity switch
      getQuestionId: () => getQuestionIdRef.current?.(),
      onPasteRecorded: (payload) => onPasteRecordedRef.current?.(payload),
      // EF-8 extras the monitor cannot see itself. `tab_focus` is the return
      // from a tab switch — the moment a photo says who came back.
      onEvent: (payload: AntiCheatPayload) => {
        if (payload.type === "fullscreen_exit") monitor?.signal("fullscreen_exit");
        else if (payload.type === "tab_focus") monitor?.signal("tab_return");
      },
      // A tab closed mid-absence: the open no-face stretch rides the final
      // beacon. No cleanup below runs on a real unload, so monitor.stop()
      // never flushes it there.
      onPageHide: () => monitor?.pageHide(),
    });
    controllerRef.current = controller;

    // Durable emitter: a fire-and-forget POST with retries, independent of
    // the controller's lifecycle (which this very cleanup disposes on every
    // Question remount). Used for the camera events that must not be lost to
    // a Next/Back racing a getUserMedia — see CP-14 in camera.ts.
    const emitDurable = (payload: AntiCheatPayload): void => {
      emitEvent(token, attemptId, payload);
    };

    let cancelled = false;
    if (!camera) {
      // Camera check off for this test: never ask, never watch — and release
      // a stream the Welcome step's optional camera check may have left open,
      // so the camera light does not stay on for a test that has no camera.
      disposeActiveStream();
    }
    void (async () => {
      if (!camera) return;
      // Camera detection piggybacks on whatever stream Gate already opened —
      // it survives Question -> Question navigation. No stream means the page
      // was reloaded mid-test (or crashed and recovered): a MediaStream never
      // survives a reload, and before CP-14 the camera then silently stayed
      // off for the rest of the attempt — which is also exactly how a Chrome
      // candidate switches cameras (the omnibox picker applies on reload).
      // Re-acquire once per page load; the helper reports the new device's
      // label (or the denial) itself, on the durable path.
      if (!getActiveStream()) {
        await resumeCameraStream(emitDurable);
      }
      if (cancelled) return;
      const stream = getActiveStream();
      if (!stream) return;
      monitor = createCameraMonitor({
        emit: controller.emit,
        emitDurable,
        // The attempt keys the ONE snapshot schedule (camera.ts): a monitor
        // remounted by a Next/Back re-holds it where it was instead of
        // starting another.
        snapshots: snapshotsRef.current
          ? {
              attemptId,
              getConfig: () => snapshotsRef.current?.getConfig() ?? null,
              upload: (blob, trigger, capturedAt, seq) =>
                uploadSnapshot(token, attemptId, blob, trigger, capturedAt, seq),
            }
          : undefined,
      });
      await monitor.start(stream);
    })();

    return () => {
      cancelled = true;
      monitor?.stop();
      controller.dispose();
      controllerRef.current = null;
      // The stream itself stays alive across Question -> Question route
      // transitions so we don't re-prompt for permission. It's only torn
      // down on full page unload via pagehide — see below.
    };
  }, [token, attemptId, camera, activity, interaction]);

  // Tear down the camera stream when the tab actually unloads. Without this,
  // the camera indicator can linger after the candidate closes the window.
  useEffect(() => {
    if (!attemptId) return;
    const onPageHide = (): void => {
      disposeActiveStream();
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [attemptId]);

  return controllerRef;
}
